import { type BoardState } from '../board'
import { overlaps } from './geometry'

/**
 * The free spot closest to `want` (top-left) for a box of size w×h, searching
 * outward in rings. Not a layout rule: it only picks where a new thing first
 * appears. Groups are containers, not obstacles, for a person (dropping a
 * thing into a frame makes it a member). `frames: 'avoid'` keeps the box clear
 * of group frames too, except the one it is going into (`into`): a thing the
 * AI places is not a member of a frame it lands on, and would look like one.
 */
export function freeSpotNear(
  s: BoardState,
  want: { x: number; y: number },
  w: number,
  h: number,
  gap = 24,
  opts: { frames?: 'ignore' | 'avoid'; into?: string | null } = {},
) {
  const obstacles = [...s.items.values()].filter((i) =>
    i.kind === 'group' ? opts.frames === 'avoid' && i.id !== opts.into : true,
  )
  const fits = (x: number, y: number) => !obstacles.some((o) => overlaps({ x, y, w, h }, o, gap))
  if (fits(want.x, want.y)) return want
  const step = 40
  for (let ring = 1; ring <= 60; ring++) {
    const candidates: { x: number; y: number; d: number }[] = []
    for (let i = -ring; i <= ring; i++) {
      for (const [dx, dy] of [
        [i, -ring],
        [i, ring],
        [-ring, i],
        [ring, i],
      ] as const) {
        candidates.push({
          x: want.x + dx * step,
          y: want.y + dy * step,
          d: Math.hypot(dx, dy * 1.4),
        })
      }
    }
    candidates.sort((a, b) => a.d - b.d)
    const hit = candidates.find((c) => fits(c.x, c.y))
    if (hit) return { x: hit.x, y: hit.y }
  }
  return want
}
